<template>
    <BModal
        :model-value="modal"
        centered
        @update:modelValue="updateModal"
        no-close-on-backdrop
        no-close-on-esc
    >
        <template #header>
            <div
                class="d-flex align-items-center justify-content-between modal-title w-100"
            >
                <h5 class="">Entrance Fee</h5>
                <BButton
                    variant="link"
                    class="close"
                    @click="updateModal(false)"
                >
                    <i class="ion ion-android-close"></i>
                </BButton>
            </div>
        </template>

        <BForm @submit.stop.prevent>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Entrance Name *"
                        label-for="name"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.name"
                            type="text"
                            id="name"
                            placeholder="Enter name"
                            :class="{
                                'is-invalid':
                                    v$.form.name.$dirty &&
                                    v$.form.name.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.name.$dirty && v$.form.name.$invalid
                            "
                        >
                            {{ v$.form.name.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol md="6">
                    <BFormGroup
                        label="Qty *"
                        label-for="qty"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.qty"
                            type="number"
                            id="qty"
                            placeholder="1"
                            :class="{
                                'is-invalid':
                                    v$.form.qty.$dirty && v$.form.qty.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="v$.form.qty.$dirty && v$.form.qty.$invalid"
                        >
                            {{ v$.form.qty.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
                <BCol md="6">
                    <BFormGroup
                        label="Amount *"
                        label-for="amount"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.amount"
                            type="number"
                            id="amount"
                            placeholder="0"
                            :class="{
                                'is-invalid':
                                    v$.form.amount.$dirty &&
                                    v$.form.amount.$invalid,
                            }"
                        />
                        <BFormInvalidFeedback
                            :state="
                                v$.form.amount.$dirty && v$.form.amount.$invalid
                            "
                        >
                            {{ v$.form.amount.$errors[0]?.$message }}
                        </BFormInvalidFeedback>
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Subtotal"
                        label-for="subtotal"
                        class="text-start mb-3"
                    >
                        <BFormInput
                            v-model="form.subtotal"
                            type="number"
                            id="subtotal"
                            placeholder="Auto Calculated"
                            disabled
                            class="bg-light"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
            <BRow>
                <BCol>
                    <BFormGroup
                        label="Description"
                        label-for="description"
                        class="text-start mb-3"
                    >
                        <BFormTextarea
                            v-model="form.description"
                            id="description"
                            placeholder="Optional note"
                            rows="3"
                        />
                    </BFormGroup>
                </BCol>
            </BRow>
        </BForm>

        <template #footer>
            <div class="d-flex justify-content-end">
                <BButton variant="primary" class="me-2" @click="handleAction"
                    >Save</BButton
                >
                <BButton variant="danger" @click="updateModal(false)"
                    >Cancel</BButton
                >
            </div>
        </template>
    </BModal>
</template>

<script setup>
import { defineProps, defineEmits, reactive, watch } from "vue";
import {
    BModal,
    BRow,
    BCol,
    BForm,
    BFormInput,
    BButton,
    BFormGroup,
    BFormInvalidFeedback,
    BFormTextarea,
} from "bootstrap-vue-next";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, minValue } from "@vuelidate/validators";
import { useToast } from "vue-toastification";

const toast = useToast();

// Props
const props = defineProps({
    action: String,
    modal: Boolean,
    selectedData: Object,
});

// Emit
const emit = defineEmits(["update:modal", "addOrUpdate"]);

// Function to update modal visibility
const updateModal = (value) => {
    emit("update:modal", value); // Sync modal state with parent
};

const resetForm = () => {
    Object.assign(form, { ...formData });
    v$.value.$reset();
};

const formData = {
    name: "",
    qty: 1,
    amount: "",
    subtotal: 0,
    description: "",
};

const form = reactive({ ...formData });

const rules = {
    form: {
        name: {
            required: helpers.withMessage("Entrance Name is required", required),
        },
        qty: {
            required: helpers.withMessage("Qty is required", required),
            minValue: helpers.withMessage(
                "Qty must be at least 1",
                minValue(1)
            ),
        },
        amount: {
            required: helpers.withMessage("Amount is required", required),
            minValue: helpers.withMessage(
                "Amount must be at least 1",
                minValue(1)
            ),
        },
    },
};

const v$ = useVuelidate(rules, { form });

// Init data jika update
watch(
    () => props.selectedData,
    (newData) => {
        if (newData && Object.keys(newData).length > 0) {
            Object.assign(form, { ...newData });
        } else {
            Object.assign(form, { ...formData });
        }
        v$.value.$reset();
    },
    { immediate: true }
);

const handleAction = () => {
    props.selectedData && props.selectedData.index !== undefined ? update() : create();
};

const create = async () => {
    // toast.clear(); // Commented out as toast might use default config
    v$.value.$touch();
    if (!v$.value.$invalid) {
        try {
            const payload = { ...form };
            delete payload.index;
            emit("addOrUpdate", payload);
            // toast.success("Entrance fee added successfully"); // Let parent or default behavior handle? 
            // TransportationForm does handle toast here. OK.
            toast.success("Entrance fee added successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to add entrance fee");
        }
    }
};

const update = async () => {
    // toast.clear();
    v$.value.$touch();
    if (!v$.value.$invalid) {
        try {
            emit("addOrUpdate", {
                index: props.selectedData.index,
                ...form,
            });
            toast.success("Entrance fee updated successfully");
            updateModal(false);
            resetForm();
        } catch (error) {
            toast.error("Failed to update entrance fee");
        }
    }
};

watch(
    () => [form.qty, form.amount],
    ([newQty, newAmount]) => {
        const qty = parseFloat(newQty) || 0;
        const amount = parseFloat(newAmount) || 0;
        form.subtotal = qty * amount;
    }
);
</script>
